import { memo, useCallback, useMemo } from 'react';
import { getChatStores } from '@/features/chats/lib/chat-store-bindings';
import { ChatWidgetToggleProps } from './ChatWidgetToggle.types';
import EmailTemplate from '@/shared/assets/images/EmailTemplate.svg';
import { OptimizedImage } from '@/shared/ui';
import DescriptionOutlined from '@/shared/assets/images/DescriptionOutlined.svg';
import RadarOutlined from '@/shared/assets/images/RadarOutlined.svg';
import ArticleOutlined from '@/shared/assets/images/ArticleOutlined.svg';
import HubOutlined from '@/shared/assets/images/HubOutlined.svg';
import { logger } from '@/lib/utils/logger';
import type {
  BehindTheAnswerWidgetSchemaType,
  StrategicTerritoriesWidgetSchemaType,
} from '@inference.engine/widgets/web';
import {
  FourCsWidgetKey,
  ImageGenerationWidgetSchemaKey,
  isHtmlWidgetKey,
} from '@inference.engine/widgets/web';
import { ImageReplyThumbnail } from './ImageReplyThumbnail';
import { getChatInfo } from '../../api/chats.api';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { personaLabel } from '@/shared/lib/persona-label';

function ArrowForwardIcon() {
  return (
    <svg viewBox="0 0 13 10" fill="currentColor" aria-hidden className="h-2.5 w-[13px] shrink-0">
      <path d="M7.78844 9.42304L6.91025 8.51921L10.093 5.3365H0V4.08654H10.093L6.91025 0.903834L7.78844 0L12.5 4.71152L7.78844 9.42304Z" />
    </svg>
  );
}

const ChatWidgetToggleComponent = ({ agentId, version, onClick }: ChatWidgetToggleProps) => {
  const { t } = useTranslation('chats');

  const { uiStore, widgetStore } = getChatStores();
  const { getActiveVersion, clearActiveVersion, switchToAgentVersion } = widgetStore.useWidgetStore(
    (state) => ({
      getActiveVersion: state.getActiveVersion,
      clearActiveVersion: state.clearActiveVersion,
      switchToAgentVersion: state.switchToAgentVersion,
    }),
  );
  const { openSidePanel, closeSidePanel } = uiStore.useUIStore.getState();
  // A widget with no layout mode is hidden by the side panel toggle, so both the panel type
  // and the layout mode are subscribed: either one changing alone flips the pressed state.
  const sidePanelType = uiStore.useSidePanelType();
  const { sidePanelLayoutMode } = uiStore.useSidePanelLayoutMode();
  const isWidgetShown = sidePanelType === 'widget' && sidePanelLayoutMode !== null;

  // The active version only counts while the panel shows it, so a click on a hidden one
  // reveals it rather than clearing it.
  const activeVersion = getActiveVersion(agentId);
  const isActive = isWidgetShown && activeVersion?.id === version.id;

  // Debug logging for handoff issues
  if (process.env.NODE_ENV === 'development') {
    logger.debug('[CHATWIDGET DEBUG]', {
      agentId,
      versionId: version.id,
      activeVersionId: activeVersion?.id,
      isActive,
      versionAgentId: version.agentId,
    });
  }

  // The card shows the agent that produced the widget; `agentId` stays the history it is filed under.
  const viewAgentId = version.widget.sourceAgentId ?? version.agentId;

  const { chatId } = useParams<{ chatId: string }>();
  // Fetch the chat title from the header
  const { data: chatInfo } = useQuery({
    queryKey: ['chatInfo', chatId],
    queryFn: () => getChatInfo({ contextId: chatId! }),
    enabled: !!chatId,
  });
  const chatTitle = chatInfo?.title || t('chats.chatWidgetToggle.reviewThe4Cs');

  // Memoize the title
  const title = useMemo(() => {
    if (viewAgentId.startsWith('audience-flow-persona')) {
      const data = version.widget.data as BehindTheAnswerWidgetSchemaType;
      return t('chats.chatWidgetToggle.interview', { personaLabel: personaLabel(data, ', ') });
    }

    if (isHtmlWidgetKey(version.widget.key ?? '')) {
      return (version.widget.data as { title?: string }).title || version.title;
    }

    // Check for FourCsWidget by widget key (works for any agent that creates it)
    if (version.widget.key === FourCsWidgetKey) {
      return chatTitle || t('chats.chatWidgetToggle.createStrategy');
    }

    // By key too: a built agent's own Generate image tool makes this widget under its minted id.
    if (version.widget.key === ImageGenerationWidgetSchemaKey) {
      const data = imageData(version.widget.data);
      return (
        data.title ||
        data.images?.[0]?.metadata?.fileName ||
        version.title ||
        t('chats.chatWidgetToggle.generatedImage')
      );
    }

    switch (viewAgentId) {
      case 'email-flow-agent':
        return (version.widget.data as { subjectLine: string }).subjectLine;
      case 'briefing-agent': {
        const items = (version.widget.data as { items: Array<{ title: string }> }).items;
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        return items?.[0]?.title ?? version.title ?? '';
      }
      case 'social-media-brief': {
        const data = (
          version.widget.data as {
            items: Array<{
              brandName: string;
              overview: {
                opportunityTitle: string;
              };
            }>;
          }
        ).items[0];
        const title = data.overview.opportunityTitle;
        return title || data.brandName || t('chats.chatWidgetToggle.brief');
      }
      case 'strategy-agent-v2': {
        return t('chats.chatWidgetToggle.strategicTerritories');
      }
      case 'strategy-agent-v3': {
        const widgetData = version.widget.data as StrategicTerritoriesWidgetSchemaType;
        return widgetData.title || version.title;
      }
      case 'copy-generation': {
        return (version.widget.data as { title: string }).title;
      }
      case 'video-generation': {
        // Minimal shape — the full `VideoWidgetData` type lives in
        // `features/video-widget`; inline-cast keeps chats free of
        // cross-feature widget-feature imports (chats canonicalization).
        const data = version.widget.data as {
          title?: string;
          video?: { metadata?: { fileName?: string } };
        };
        return (
          data.title ||
          data.video?.metadata?.fileName ||
          version.title ||
          t('chats.chatWidgetToggle.generatedVideo')
        );
      }
      default:
        return version.title;
    }
  }, [version, viewAgentId, chatTitle, t]);

  // Memoize the subtitle
  const subtitle = useMemo(() => {
    if (viewAgentId.startsWith('audience-flow-persona')) {
      return t('chats.chatWidgetToggle.dataBehindTheAnswer');
    }

    if (version.widget.key === FourCsWidgetKey) {
      return t('chats.chatWidgetToggle.createStrategy');
    }
    return null;
  }, [version, viewAgentId, t]);

  // Memoize the click handler to prevent unnecessary re-renders
  const handleClick = useCallback(() => {
    if (onClick) {
      onClick();
    }
    if (!isActive) {
      // Switch to this agent and version, clearing all others
      switchToAgentVersion(agentId, version.id);
      openSidePanel('widget');
    } else {
      // Clear the active version when closing the panel
      clearActiveVersion(agentId);
      closeSidePanel();
    }
  }, [
    onClick,
    switchToAgentVersion,
    clearActiveVersion,
    agentId,
    version.id,
    openSidePanel,
    closeSidePanel,
    isActive,
  ]);

  const imageTemplate = useMemo(() => {
    // Account for abnormal agent ids like audience-flow-persona-1, audience-flow-persona-2, etc.
    if (viewAgentId.startsWith('audience-flow-persona')) {
      return HubOutlined;
    }
    switch (viewAgentId) {
      case 'email-flow-agent':
        return EmailTemplate;
      case 'insight-agent':
        return RadarOutlined;
      case 'briefing-agent':
        return DescriptionOutlined;
      case 'social-media-brief':
        return DescriptionOutlined;
      case 'copy-generation':
        return ArticleOutlined;
      default:
        return EmailTemplate;
    }
  }, [viewAgentId]);

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-pressed={isActive}
      className={`flex w-full flex-row items-center gap-4 rounded-2xl border border-border-strong p-4 text-left transition-colors duration-slow ease-out ${
        isActive ? 'bg-accent' : 'bg-transparent hover:bg-muted'
      }`}
    >
      {version.widget.key === ImageGenerationWidgetSchemaKey ? (
        <ImageReplyThumbnail images={imageData(version.widget.data).images ?? []} />
      ) : (
        <div className="shrink-0">
          <OptimizedImage
            src={imageTemplate}
            width={24}
            height={24}
            alt={t('chats.chatWidgetToggle.widgetIcon')}
            priority
          />
        </div>
      )}
      <div className="flex flex-col">
        <div className="block w-full">
          <span className="text-base font-medium leading-6 text-foreground">{title}</span>
          {subtitle && (
            <div>
              <span className="text-xs leading-4 text-muted-foreground">{subtitle}</span>
            </div>
          )}
        </div>
        {viewAgentId === 'email-flow-agent' && (
          <span className="text-xs leading-4 text-muted-foreground">
            {t('chats.chatWidgetToggle.version', { version: version.version })}
          </span>
        )}
        {viewAgentId === 'strategy-agent-v3' && version.widget.key !== FourCsWidgetKey && (
          <span className="text-xs leading-4 text-muted-foreground">
            {t('chats.chatWidgetToggle.strategicTerritories')}
          </span>
        )}
      </div>
      {/* Decorative: the whole row is the control, so the affordance carries no separate action. */}
      <span className="ml-auto flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground">
        <ArrowForwardIcon />
      </span>
    </button>
  );
};

/**
 * Minimal shape: the full `ImageGenerationWidget` type lives in `features/image-widget`, and an
 * inline cast keeps chats free of cross-feature widget imports.
 */
function imageData(data: unknown) {
  return data as {
    title?: string;
    images?: Array<{ assetId: string; metadata?: { fileName?: string } }>;
  };
}

// Enhanced memoization optimization with more granular prop comparison
const arePropsEqual = (prevProps: ChatWidgetToggleProps, nextProps: ChatWidgetToggleProps) => {
  return (
    prevProps.agentId === nextProps.agentId &&
    prevProps.version.id === nextProps.version.id &&
    prevProps.version.agentId === nextProps.version.agentId && // Important for agent-specific styling
    prevProps.version.title === nextProps.version.title &&
    prevProps.version.version === nextProps.version.version &&
    prevProps.onClick === nextProps.onClick &&
    prevProps.forceActive === nextProps.forceActive
  );
};

export const ChatWidgetToggle = memo(ChatWidgetToggleComponent, arePropsEqual);
